/**
 * Clique num eco do radar: lê a cor do pixel no PNG ORIGINAL (não no bitmap
 * reduzido para a tela, onde a borda de uma mancha mistura cores) e devolve a
 * classe da legenda — o valor que o SIPAM publicou, sem estimativa nossa.
 *
 * Sem eco no ponto (pixel transparente), não responde: o clique segue para o
 * município, como sem o radar.
 */
import type OlMap from 'ol/Map';
import { createElement } from 'react';
import { registerCoordinatePopup } from '../map/popupControl';
import { mapToLonLat } from '../map/projection';
import { modeloDe, radarCache } from './cache';
import { pixelDoPonto, type ModeloOrigem } from './geometria';
import { usePlanoRadar } from './motor';
import { urlDe } from './plano';
import { classeDaCor, coresDoRadar, DEF_PRODUTO } from './produtos';
import { RadarPopup } from './RadarPopup';
import { bitmapFiel } from './reprojetar.worker';
import { useRadarStore } from './store';

/** RGBA do pixel do PNG que contém o ponto lon/lat. */
async function lerPixel(blob: Blob, lon: number, lat: number, modelo: ModeloOrigem) {
  const bmp = await bitmapFiel(blob);
  try {
    const px = pixelDoPonto(modelo, bmp.width, bmp.height, lon, lat);
    if (!px) return null;
    const canvas = document.createElement('canvas');
    canvas.width = 1;
    canvas.height = 1;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return null;
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(bmp, px[0], px[1], 1, 1, 0, 0, 1, 1);
    return ctx.getImageData(0, 0, 1, 1).data;
  } finally {
    bmp.close();
  }
}

export function instalarLeituraDoPixel(_map: OlMap): () => void {
  return registerCoordinatePopup(async ([x, y]) => {
    const { visivel, manifesto, produto, noQuadro } = useRadarStore.getState();
    const plano = usePlanoRadar.getState().plano;
    if (!visivel || !manifesto || !plano) return null;
    const [lon, lat] = mapToLonLat(x, y);
    if (!Number.isFinite(lon) || !Number.isFinite(lat)) return null;
    const def = DEF_PRODUTO[produto];
    for (const r of manifesto.radares) {
      const q = noQuadro[r.id];
      if (q?.estado !== 'ok' || q.varredura === null) continue;
      const modelo = modeloDe(r.id);
      if (!modelo || !pixelDoPonto(modelo, 1, 1, lon, lat)) continue;
      const item = plano.radares.find((p) => p.id === r.id);
      const blob = item ? radarCache.blob(urlDe(item, q.varredura)) : null;
      if (!blob) continue;
      const rgba = await lerPixel(blob, lon, lat, modelo);
      if (!rgba || rgba[3] === 0) continue;
      // Casamento exato, com a paleta em que ESTE radar grava (Belém tem a sua).
      const classe = classeDaCor(def, rgba[0], rgba[1], rgba[2], 0, coresDoRadar(r.id, produto));
      return createElement(RadarPopup, { produto, classe, lonLat: [lon, lat] });
    }
    return null;
  });
}
